import { ReactNode, useMemo } from "react";

import {
  Active,
  Announcements,
  DndContext,
  DragOverlay,
  KeyboardCode,
  KeyboardSensor,
  Over,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { TFunction } from "i18next";

import { DocumentGroup } from "@/api";
import { useClientId } from "@/common/hooks/useClientId";
import { useTranslation } from "@/translations";

import { DocumentDndContext } from "./DocumentDndContext";
import { DragOverlayContent } from "./DragOverlayContent";
import { DragData } from "./DropZoneDnd.types";
import { PendingDocumentMovesContext } from "./PendingDocumentMovesContext";
import { usePendingMovesStore } from "./hooks/usePendingMovesStore";
import { documentDndKeyboardCoordinates } from "./keyboardCoordinates";
import { useDocumentDnd } from "./useDocumentDnd";

interface Props {
  children: ReactNode;
}

const KEYBOARD_CODES = {
  start: [KeyboardCode.Space],
  cancel: [KeyboardCode.Esc],
  end: [KeyboardCode.Space, KeyboardCode.Enter, KeyboardCode.Tab],
};

function getZoneName(t: TFunction, zone: DocumentGroup): string {
  switch (zone) {
    case DocumentGroup.Counselor:
      return t("dropzone.dnd.zoneCounselor");
    case DocumentGroup.Client:
      return t("dropzone.dnd.zoneClient");
    case DocumentGroup.ClientSelf:
      return t("dropzone.dnd.zoneClientSelf");
    case DocumentGroup.CaseNote:
      return t("dropzone.dnd.zoneCaseNote");
    case DocumentGroup.AssetInventoryDependants:
      return t("dropzone.dnd.zoneAssetInventoryDependants");
    case DocumentGroup.AssetInventoryBankStatement:
      return t("dropzone.dnd.zoneAssetInventoryBankStatement");
    case DocumentGroup.AssetInventoryIncome:
      return t("dropzone.dnd.zoneAssetInventoryIncome");
    case DocumentGroup.AssetInventoryExpenses:
      return t("dropzone.dnd.zoneAssetInventoryExpenses");
    case DocumentGroup.AssetInventoryOwnClaims:
      return t("dropzone.dnd.zoneAssetInventoryOwnClaims");
    case DocumentGroup.AssetInventoryDomesticRealEstate:
      return t("dropzone.dnd.zoneAssetInventoryDomesticRealEstate");
    case DocumentGroup.AssetInventoryForeignRealEstate:
      return t("dropzone.dnd.zoneAssetInventoryForeignRealEstate");
    case DocumentGroup.AssetInventoryBuildingSavingsContracts:
      return t("dropzone.dnd.zoneAssetInventoryBuildingSavingsContracts");
    case DocumentGroup.AssetInventorySavingsAccounts:
      return t("dropzone.dnd.zoneAssetInventorySavingsAccounts");
    case DocumentGroup.AssetInventoryLifeInsurances:
      return t("dropzone.dnd.zoneAssetInventoryLifeInsurances");
    case DocumentGroup.AssetInventoryPensionInsurances:
      return t("dropzone.dnd.zoneAssetInventoryPensionInsurances");
    case DocumentGroup.AssetInventorySecuritiesDepots:
      return t("dropzone.dnd.zoneAssetInventorySecuritiesDepots");
    case DocumentGroup.AssetInventoryCashAndBankAssets:
      return t("dropzone.dnd.zoneAssetInventoryCashAndBankAssets");
    case DocumentGroup.AssetInventoryVehicles:
      return t("dropzone.dnd.zoneAssetInventoryVehicles");
    case DocumentGroup.AssetInventoryBuildingsOnForeignLand:
      return t("dropzone.dnd.zoneAssetInventoryBuildingsOnForeignLand");
    case DocumentGroup.AssetInventoryTenancyRights:
      return t("dropzone.dnd.zoneAssetInventoryTenancyRights");
    case DocumentGroup.AssetInventoryCompanyShares:
      return t("dropzone.dnd.zoneAssetInventoryCompanyShares");
    case DocumentGroup.AssetInventoryCooperativeMemberships:
      return t("dropzone.dnd.zoneAssetInventoryCooperativeMemberships");
    case DocumentGroup.AssetInventoryOtherPropertyRights:
      return t("dropzone.dnd.zoneAssetInventoryOtherPropertyRights");
    case DocumentGroup.AssetInventoryDonations:
      return t("dropzone.dnd.zoneAssetInventoryDonations");
  }
}

function getFileName(active: Active): string {
  return (active.data.current as DragData | undefined)?.file.fileName ?? "";
}

function getOverZoneName(t: TFunction, over: Over | null): string | null {
  return over === null ? null : getZoneName(t, over.id as DocumentGroup);
}

/**
 * Hosts the single document DndContext spanning the dropzone panel and the routed page content,
 * so files can be dragged between the panel zones and the asset inventory upload areas.
 * Panel↔panel drops move immediately; drops touching an asset inventory zone are routed to the
 * mounted view as pending form changes (see PendingDocumentMovesContext). Drags work with the
 * pointer and the keyboard (arrow keys jump between the allowed zones), with localized screen
 * reader announcements.
 */
export function DocumentDndProvider({ children }: Props) {
  const { t } = useTranslation();
  const clientId = useClientId();
  const { contextValue: pendingMovesValue, tryAddPendingMove } = usePendingMovesStore();
  const { activeDrag, isMoving, handleDragStart, handleDragEnd, handleDragCancel } = useDocumentDnd(
    clientId,
    tryAddPendingMove
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: documentDndKeyboardCoordinates, keyboardCodes: KEYBOARD_CODES })
  );

  const dndValue = useMemo(() => ({ activeDrag, isMoving }), [activeDrag, isMoving]);

  const accessibility = useMemo(() => {
    const announcements: Announcements = {
      onDragStart: ({ active }) => t("dropzone.dnd.srPickedUp", { fileName: getFileName(active) }),
      onDragOver: ({ active, over }) => {
        const zone = getOverZoneName(t, over);
        return zone === null
          ? t("dropzone.dnd.srNotOverZone", { fileName: getFileName(active) })
          : t("dropzone.dnd.srOverZone", { fileName: getFileName(active), zone });
      },
      onDragEnd: ({ active, over }) => {
        const zone = getOverZoneName(t, over);
        return zone === null
          ? t("dropzone.dnd.srDropCancelled", { fileName: getFileName(active) })
          : t("dropzone.dnd.srDroppedInZone", { fileName: getFileName(active), zone });
      },
      onDragCancel: ({ active }) => t("dropzone.dnd.srDropCancelled", { fileName: getFileName(active) }),
    };

    return {
      announcements,
      screenReaderInstructions: { draggable: t("dropzone.dnd.srInstructions") },
    };
  }, [t]);

  return (
    <DndContext
      sensors={sensors}
      accessibility={accessibility}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <DocumentDndContext.Provider value={dndValue}>
        <PendingDocumentMovesContext.Provider value={pendingMovesValue}>
          {children}
        </PendingDocumentMovesContext.Provider>
      </DocumentDndContext.Provider>
      <DragOverlay dropAnimation={null}>
        {activeDrag ? <DragOverlayContent file={activeDrag.file} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
